import { Modifier, ModifierGroupInterface, ProductInterface } from '../../services/exports/Interfaces';
import ModifierItem from './Modifier';
import { useTranslation } from 'react-i18next';
import collect from 'collect.js';
import BasketHelpers from '../../services/helpers/BasketHelpers';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { BasketLineItemModifier } from '../../store/types';
import classNames from 'classnames';
import classnames from 'classnames';
import { CompiledModifier } from '../../providers/MenuProvider';
import { AlertCircle } from 'lucide-react';
import useCategoryRestriction from '../../hooks/menu/useCategoryRestriction';

interface Props {
  data: ModifierGroupInterface<CompiledModifier>;
  selectedModifiers: BasketLineItemModifier[];
  toggle: (entry: ProductInterface | Modifier) => void;
  openSubItem: (entry: ProductInterface | Modifier) => void;
  adjustQuantity: (entry: ProductInterface | Modifier, step: number) => void;
  isUpsell?: boolean;
  hasError?: boolean;
}

const ModifierGroup = forwardRef((props: Props, ref) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:ModifierGroup' });
  const { data, selectedModifiers, toggle, openSubItem, adjustQuantity, isUpsell, hasError } = props;
  const isSelected = useCallback(
    (modifier: Modifier | ProductInterface): boolean =>
      !!collect(selectedModifiers).first(
        (item) =>
          item.modifier_group_id === data.id &&
          item.modifier_id === modifier.id &&
          item.modifier_type === modifier.type,
      ),
    [data.id, selectedModifiers],
  );
  const getQuantity = (modifier: Modifier | ProductInterface): number =>
    collect(selectedModifiers).first(
      (item) =>
        item.modifier_group_id === data.id && item.modifier_id === modifier.id && item.modifier_type === modifier.type,
    )?.quantity ?? 0;

  const isRequired = useMemo(() => BasketHelpers.isModifierGroupRequired(data), [data]);

  const hasSelectedModifierGroup = useMemo(
    () =>
      collect(selectedModifiers).contains(
        (item) => item.modifier_group_id === data.id && item.modifier_type === 'modifier',
      ),
    [selectedModifiers, data.id],
  );

  function renderTitle() {
    const title = data.is_variant ? t('select_size') : data.name;

    const getMandatoryLabel = (): string => {
      if (isUpsell) {
        return t('required');
      }

      return isRequired ? t('required') : t('optional');
    };

    return (
      <div
        className={classNames('px-md py-sm bg-gray-500 dark:bg-brand-card', { 'bg-brand-primary/30 py-xs': isUpsell })}
      >
        <div className="flex flex-col space-y-[1px]">
          <h6 className="mr-sm">{title}</h6>
          <div className="flex flex-row items-center gap-x-1 text-muted-foreground text-xs">
            <span
              className={classnames({
                'text-[#BF9F68]': !hasSelectedModifierGroup && isRequired,
                'text-primary': hasSelectedModifierGroup && isRequired,
              })}
            >
              {getMandatoryLabel()}
            </span>
            {!isUpsell && data.max_count > 1 && (
              <>
                <span className="mx-1">•</span>
                <span>{t('select_up_to', { maxCount: data.max_count })}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const toggleModifier = (entry: Modifier | ProductInterface): void => {
    const isSubItem = !!entry.modifier_group_ids?.length;

    if (!isSubItem) {
      toggle(entry);

      return;
    }

    openSubItem(entry);
  };

  const getNestedModifiers = (modifier: ProductInterface | Modifier): BasketLineItemModifier[] =>
    collect(selectedModifiers).first(
      (item) =>
        item.modifier_group_id === data.id && item.modifier_id === modifier.id && item.modifier_type === modifier.type,
    )?.modifiers;

  const isIncreasable = useMemo(
    (): boolean =>
      !data.max_count || (collect(selectedModifiers).sum((item) => item.quantity) as number) < data.max_count,
    [selectedModifiers, data.max_count],
  );

  const getRestriction = useCategoryRestriction();

  const restrictedTooltipByModifierId = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();

    for (const modifier of data.modifiers) {
      const { isRestricted, tooltip } = getRestriction(modifier.category_ids);

      if (isRestricted) {
        map.set(modifier.id, tooltip);
      }
    }

    return map;
  }, [data.modifiers, getRestriction]);

  return (
    // `data-has-error` is what makes the required-group gate assertable. `hasError` is driven by
    // MenuItemModal's `showValidation`, which only flips once the save button has been pressed with a
    // required group unfilled — so the marker distinguishes "not filled in yet" from "rejected", which
    // otherwise look identical in the DOM. `data-required` is exposed too, since a spec must not
    // assume WHICH seeded group is the required one.
    <div
      ref={ref as React.LegacyRef<HTMLDivElement>}
      data-test={`modifier-group-${data.id}`}
      data-required={BasketHelpers.isModifierGroupRequired(data) ? 'true' : 'false'}
      data-has-error={hasError ? 'true' : 'false'}
    >
      {renderTitle()}
      {hasError && (
        <div className="px-md pt-sm">
          <div className={errorNoticeContainer} data-test={`modifier-group-error-${data.id}`}>
            <AlertCircle className="w-4 h-4 text-danger shrink-0 mt-[2px]" />
            <p className="text-xs text-brand-text-primary">{t('required_missing')}</p>
          </div>
        </div>
      )}
      <div className={classNames('px-md py-sm [&>*]:pb-2xs')}>
        {data.modifiers.map((modifier) => {
          const restrictedTooltip = restrictedTooltipByModifierId.get(modifier.id);

          return (
            <ModifierItem
              key={`modifier-group-${data.id}-${modifier.type}-${modifier.id}`}
              data={modifier}
              quantity={getQuantity(modifier)}
              isSelected={isSelected(modifier)}
              isSingleSelect={data.max_count === 1}
              isDeselectable={data.max_count === 1 && !isRequired}
              hasMultiply={data.multiply}
              toggle={toggleModifier}
              deselect={toggle}
              adjustQuantity={(step) => adjustQuantity(modifier, step)}
              selectedModifiers={getNestedModifiers(modifier)}
              isIncreasable={isIncreasable}
              isUpsell={isUpsell}
              isRestricted={Boolean(restrictedTooltip)}
              restrictedTooltip={restrictedTooltip}
            />
          );
        })}
      </div>
    </div>
  );
});

export default ModifierGroup;

const errorNoticeContainer =
  'flex items-start gap-[10px] rounded-lg border border-danger/30 bg-danger/10 px-[14px] py-[10px]';
